/**
 * First-touch campaign signup attribution.
 *
 * Reads UTM params on first landing and persists them client-side so they survive
 * SPA navigation and the OAuth provider round-trip, then surfaces them at signup as
 * signup_utm_* PostHog person properties (which the PostHog -> Customer.io destination
 * forwards as profile attributes) and sign-up event properties.
 *
 * First-touch wins: a later visit never overwrites the original channel. Storage is
 * treated as first-party functional data (written before the consent banner is
 * answered), so direct-to-lureo.app campaign links are attributed even pre-consent.
 */

const STORAGE_KEY = 'lureo_attribution';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

// source + campaign are the two channels we use today; medium/term/content ride along
// for free so adding them to campaign links later needs no code change.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
type UtmKey = (typeof UTM_KEYS)[number];

interface StoredAttribution {
  utm: Partial<Record<UtmKey, string>>;
  landing_path: string;
  referrer: string;
  first_seen_at: number;
}

/** Read + TTL-check the stored first-touch record. Returns null when absent/expired. */
function getStoredAttribution(): StoredAttribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (!parsed?.first_seen_at || Date.now() - parsed.first_seen_at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Capture first-touch UTM from the current URL. Call once on app startup, before any
 * SPA navigation can strip the query string. No-op if a record already exists (first
 * touch wins) or if this landing carries no UTM (so a genuine later first-touch wins).
 */
export function captureFirstTouchAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    if (getStoredAttribution()) return;

    const params = new URLSearchParams(window.location.search);
    const utm: Partial<Record<UtmKey, string>> = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) utm[key] = value.slice(0, 200);
    }
    if (Object.keys(utm).length === 0) return;

    const record: StoredAttribution = {
      utm,
      landing_path: window.location.pathname,
      referrer: document.referrer || '',
      first_seen_at: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage blocked/unavailable — attribution is best-effort, never fatal.
  }
}

/**
 * Map stored first-touch attribution to signup_utm_* properties for use as PostHog
 * person properties and sign-up event properties. Returns {} when there is none, so
 * callers can spread it unconditionally.
 */
export function getSignupAttributionProperties(): Record<string, string> {
  const stored = getStoredAttribution();
  if (!stored) return {};
  const props: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = stored.utm[key];
    if (value) props[`signup_${key}`] = value;
  }
  return props;
}

/**
 * Raw first-touch source + campaign for server-side persistence (org_members columns).
 * Independent of cookie/analytics consent: reads the same first-party functional record,
 * so it works for users who declined the analytics cookie (whom PostHog never sees).
 * Returns nulls when there is no stored attribution.
 */
export function getStoredSignupUtm(): { utmSource: string | null; utmCampaign: string | null } {
  const stored = getStoredAttribution();
  return {
    utmSource: stored?.utm.utm_source ?? null,
    utmCampaign: stored?.utm.utm_campaign ?? null,
  };
}
